/* eslint-disable max-lines-per-function */
import { useCallback, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { getLocalSectionData } from 'src/app/pages/homepage/shared/api';
import { useAutocompleteService } from 'src/app/shared/hooks/googlePlaces';
import { useCountryCode, useLocale } from 'src/app/shared/hooks/l10n';
import { useMappedLoadedState } from 'src/app/shared/hooks/state';
import { isLoaded } from 'src/app/shared/utils/async';

import { getGooglePlaces } from './api';

type Location = Awaited<ReturnType<typeof getGooglePlaces>>[number];

export function useEditLocationModal({ closeModal }: { closeModal: () => void }) {
	const userLocale = useLocale();
	const userCountry = useCountryCode();
	const autocompleteService = useAutocompleteService();
	const utilityContext = useUtilityContext();
	const { translate } = useI18n();

	const [placeInputValue, setPlaceInputValue] = useState('');
	const [placeValue, setPlaceValue] = useState<Location | null>(null);
	const [formError, setFormError] = useState<string | null>(null);

	const autocompleteQuery = useQuery({
		queryKey: [placeInputValue, userLocale, autocompleteService, userCountry],
		queryFn: async () =>
			getGooglePlaces({ text: placeInputValue, language: userLocale, country: userCountry }, autocompleteService),
		enabled: !!autocompleteService && placeInputValue.length > 0,
	});

	const placesState = useMappedLoadedState(autocompleteQuery, ({ data }) => ({ value: data }));
	const places = useMemo(() => (isLoaded(placesState) ? placesState.value : []), [placesState]);

	const handlePlaceInputChange = useCallback(
		(newInputValue: string) => {
			if (formError) {
				setFormError(null);
			}
			setPlaceInputValue(newInputValue);
		},
		[formError],
	);

	const handlePlaceChange = useCallback((selectedPlace: Location | null) => {
		setPlaceValue(selectedPlace);
	}, []);

	const queryClient = useQueryClient();
	const { mutate, reset } = useMutation({
		mutationFn: async (location: { city: string; stateCode: string; countryCode: string }) =>
			getLocalSectionData(location, userLocale, utilityContext),
		onSuccess: (data) => {
			// NOTE: Updates the location for local section.
			// This implementation currently only supports the 'us' country and 'en-us' locale
			// The edit local section feature is built specifically for the current U.S. configuration
			// Slug creation and query logic are tied to 'us' and 'en-us' values
			// Extending this to support additional countries or locales will require:
			//     - Introducing a localization strategy for country/locale mappings
			//     - Refactoring the slug generation and query logic to be dynamic
			// TODO: Support additional countries/locales beyond 'us' and 'en-us'
			//       See ticket: https://change.atlassian.net/jira/software/c/projects/CHANGE/boards/375?selectedIssue=CHANGE-74243
			queryClient.setQueryData([getLocalSectionData.queryKey], () => data);
			closeModal();
		},
		onError: () => {
			setFormError(
				translate('corgi.homepage.local_section.modal.body.search_input.errors.no_city_data', {
					userInput: placeValue?.name,
				}),
			);
		},
	});

	const handleLocationSubmit = useCallback(() => {
		if (!placeValue) {
			setFormError(translate('corgi.homepage.local_section.modal.body.search_input.errors.no_input'));
			return;
		}

		const [city, stateCode, country] = placeValue.name.split(', ');
		// TODO: add support for non-us locations
		// google autocomplete seems to return the 3-letter country code (unverified), but our API only expects the 2-letter country code
		// this is a temporary fix until we add additional support for non-us locations
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- not all countries have a separator for each part
		const countryCode = country?.toLowerCase() === 'usa' ? 'us' : country;
		const location = { city, stateCode, countryCode };

		mutate(location);
	}, [placeValue, mutate, translate]);

	const handleModalClose = useCallback(() => {
		reset();
		setFormError(null);
		closeModal();
	}, [closeModal, reset]);

	return {
		data: {
			placeInputValue,
			placeValue,
			places,
			formError,
		},
		actions: {
			handlePlaceInputChange,
			handlePlaceChange,
			handleLocationSubmit,
			handleModalClose,
		},
	};
}

export { getGooglePlaces };
